import { useMemo } from "react";
import {
	Settings2,
	Layers,
	Users,
	Building2,
	MapPin,
	Building,
	User,
	ListChecks,
	Rows3,
	Briefcase,
	Presentation,
	UsersRound,
	FileText,
	CircleDollarSign,
	Gift,
	Timer,
	Calculator,
	Home,
	Copy,
	Clock,
	HandHelping,
	Network,
	Receipt,
	Table,
	Phone,
	QrCode,
	Workflow,
	UserRound,
	BriefcaseBusiness,
	UserCheck,
	Landmark,
	Type,
	Book,
	ListTodo,
	ListTree,
	CalendarDays,
	ClipboardList,
	FolderTree,
	FileCode,
	UserCog,
	Images,
	Mail,
	CalendarCheck,
	Coins,
	CalendarRange,
	Calendar,
	Coffee,
	MessageSquare,
	MessageCircle,
	Bell,
	Banknote,
	FileOutput,
	IdCard,
	BookUser,
	Sparkles,
	Award,
	Square,
	TrendingUp,
	QrCodeIcon,
	Cog,
	LayoutTemplate,
	FolderSync,
} from "lucide-react";
import { useUser } from "../../../utils/hooks/user";
import { getViewPermissions } from "../../../utils/helpers/access";
import { useAccess, useAccessInList } from "../../../utils/hooks/access";
import { useCompany } from "../../../utils/hooks/company";
import { getRequestModes, hasAnyRequestAccess } from "../../../features/Request/permissions";
import { usePreferences } from "../../../features/Settings/Configuration/hooks/usePreferences";

export const useSidebarConfig = () => {
	const user = useUser();
	const accessMap = user?.permissions;
	const company = useCompany();
	const calculateOverMultipleProfiles = usePreferences("timeSheetGeneral.calculateOverMultipleProfiles");
	const hideRetroAdjustment = usePreferences("timeSheetGeneral.hideRetroAdjustment");

	const modules = useMemo(() => {
		return company?.settings?.modules || [];
	}, [company?.settings?.modules]);

	const generalSettings = useMemo(
		() =>
			accessMap?.settings?.general
				? getViewPermissions(accessMap?.settings?.general, "settings.general")
				: [],
		[accessMap?.settings?.general]
	);

	const { hasAccess: canViewGeneralSettings } =
		useAccessInList(generalSettings);

	const { hasAccess: canViewConfiguration } = useAccessInList([
		'settings.configuration.canView',
		'settings.configuration.rate.canView',
	]);

	const { hasAccess: canManageUsers } = useAccess('canManageUsers');

	const timeClockPermissions = useMemo(
		() =>
			accessMap?.clockin
				? getViewPermissions(accessMap?.clockin, 'clockin')
				: [],
		[accessMap?.clockin]
	);
	const { hasAccess: canViewClockin } = useAccessInList(timeClockPermissions);

	const { hasAccess: canViewTimesheet } = useAccessInList([
		'timeSheet.canViewMyTimesheet',
		'timeSheet.canView',
	]);

	const { hasAccess: canViewScheduler } = useAccessInList([
		"schedule.canView",
		"schedule.canViewMySchedule"
	])

	const requestAccess = useMemo(() => ({
		hasAny: hasAnyRequestAccess(accessMap),
		clock: getRequestModes(accessMap, "clock"),
		pto: getRequestModes(accessMap, "pto"),
		schedule: getRequestModes(accessMap, "schedule"),
		availability: getRequestModes(accessMap, "availability"),
		timesheet: getRequestModes(accessMap, "timesheet"),
	}), [accessMap]);

	const canViewRequest = requestAccess.hasAny;
	const canViewClockRequest = requestAccess.clock.my || requestAccess.clock.team;
	const canViewPtoRequest = requestAccess.pto.my || requestAccess.pto.team;
	const canViewScheduleRequest = requestAccess.schedule.my || requestAccess.schedule.team;
	const canViewAvailabilityRequest = requestAccess.availability.my || requestAccess.availability.team;
	const canViewTimesheetRequest = requestAccess.timesheet.my || requestAccess.timesheet.team;

	const { hasAccess: canViewTimeOff } = useAccessInList([
		"timeOff.canViewMyTeamTimeOff",
		"timeOff.canViewMyTimeOff"
	]);

	const payPoliciesSettings = useMemo(
		() =>
			accessMap?.settings?.payPolicies
				? getViewPermissions(
					accessMap?.settings?.payPolicies,
					'settings.payPolicies'
				)
				: [],
		[accessMap?.settings?.payPolicies]
	);
	const { hasAccess: canViewPayPolicies } =
		useAccessInList(payPoliciesSettings);

	const integrationSettings = useMemo(
		() =>
			accessMap?.settings?.integration
				? getViewPermissions(
					accessMap?.settings?.integration,
					'settings.integration'
				)
				: [],
		[accessMap?.settings?.integration]
	);

	const { hasAccess: canViewIntegration } =
		useAccessInList(integrationSettings);

	const { hasAccess: canViewClockinSettings } = useAccessInList([
		"device.canView",
		"qrCode.canView",
		"whitelistNumber.canView",
		"settings.flyer.canView",
		"settings.attestation.canView"
	]);

	const { hasAccess: canViewGroup } = useAccessInList([
		"settings.general.organizationLevelGroup.canView",
		"settings.general.organizationLevelGroup.organizationLevel.canView",
	]);

	const customFieldSettings = useMemo(
		() =>
			accessMap?.settings?.general?.customFields
				? getViewPermissions(
					accessMap?.settings?.general?.customFields,
					'settings.general.customFields'
				)
				: [],
		[accessMap?.settings?.general?.customFields]
	);

	const { hasAccess: canViewCustomFields } =
		useAccessInList(customFieldSettings);

	const ruleSettings = useMemo(
		() =>
			accessMap?.settings?.rules
				? getViewPermissions(accessMap?.settings?.rules, 'settings.rules')
				: [],
		[accessMap?.settings?.rules]
	);

	const schedulingSettings = useMemo(
		() =>
			accessMap?.settings?.scheduling
				? getViewPermissions(
					accessMap?.settings?.scheduling,
					'settings.scheduling'
				)
				: [],
		[accessMap?.settings?.scheduling]
	);

	const { hasAccess: canViewUserDemographics } = useAccess("settings.userDemographic.canView");

	const { hasAccess: canViewRules } = useAccessInList(ruleSettings);
	const { hasAccess: canViewScheduling } = useAccessInList(schedulingSettings);

	const { hasAccess: canViewReports } = useAccess("reports.canView");

	const { hasAccess: canViewBillingRate } = useAccess("billingRate.invoices.canView");

	const canViewCalRules = useAccess("settings.payPolicies.calculationGroup.canView");

	const { hasAccess: canViewCommunication } = useAccess('communication.canView')

	return useMemo(
		() => [
			{
				index: true,
				title: 'dashboard',
				icon: Home,
				permissions: ['dashboard.canView'],
			},
			{
				path: 'people',
				icon: User,
				permissions: ['people.canView'],
			},
			{
				path: 'time-clock',
				icon: Clock,
				access: canViewClockin && modules.includes("timeClock"),
				children: canManageUsers
					? [
						{
							index: true,
							path: 'team',
							title: 'my-team',
							icon: Users,
							permissions: ['canManageUsers'],
						},
						{
							path: 'my',
							title: 'my-shift',
							icon: UserRound,
							permissions: ['clockin.canView'],
						},
						{
							path: 'original-clocks',
							title: 'original-clocks',
							icon: BriefcaseBusiness,
							permissions: ['clockin.canViewOriginalClock'],
						},
					]
					: [
						{
							index: true,
							path: 'my',
							title: 'my-shift',
							icon: UserRound,
							permissions: ['clockin.canView'],
						},
						{
							path: 'original-clocks',
							title: 'original-clocks',
							icon: BriefcaseBusiness,
							permissions: ['clockin.canViewOriginalClock'],
						},
					],
			},
			{
				path: 'time-sheet',
				icon: Copy,
				access: canViewTimesheet && modules.includes("timesheet"),
				children: canManageUsers
					? [
						{
							index: true,
							path: "team",
							title: "my-team",
							icon: Users,
							permissions: ["timeSheet.canView"],
						},
						{
							path: "weekly",
							title: "weekly",
							icon: CalendarRange,
							permissions: ["timeSheet.canView"],
						},
						{
							path: "manager-view",
							title: "manager-view",
							icon: ListTree,
							permissions: ["timeSheet.canView"],
						},
						calculateOverMultipleProfiles && {
							path: "overtimes",
							title: "overtimes",
							icon: TrendingUp,
							permissions: ["timeSheet.canViewMultipleProfiles"],
						},
						(user?.otherProfileIds?.length > 0 && calculateOverMultipleProfiles) && {
							path: "my-overtimes",
							title: "my-overtimes",
							icon: Table,
							permissions: ["timeSheet.canViewMyMultipleProfiles"],
						},
						(!hideRetroAdjustment && {
							path: "retro-adjustment",
							title: "retro-adjustment",
							icon: Calendar,
							permissions: ["timeSheet.canViewRetro"]
						}),
						{
							path: "my",
							title: "my-timesheet",
							icon: UserRound,
							permissions: ["timeSheet.canViewMyTimesheet"],
						},
					]?.filter(Boolean) : [
						{
							index: true,
							path: 'my',
							title: 'my-timesheet',
							icon: UserRound,
							permissions: ['timeSheet.canViewMyTimesheet'],
						},
						(user?.otherProfileIds?.length > 0 && calculateOverMultipleProfiles) && {
							path: "my-overtimes",
							title: "my-overtimes",
							icon: Table,
							permissions: ["timeSheet.canViewMyMultipleProfiles"],
						},
					]?.filter(Boolean),
			},
			// {
			// 	path: "new-time-sheet",
			// 	icon: Copy,
			// 	access: canViewTimesheet && modules.includes("timesheet"),
			// 	children: canManageUsers
			// 		? [
			// 			{
			// 				index: true,
			// 				path: "team",
			// 				title: "my-team",
			// 				icon: Users,
			// 				permissions: ["timeSheet.canView"],
			// 			},
			// 			{
			// 				path: "weekly",
			// 				title: "weekly",
			// 				icon: CalendarRange,
			// 				permissions: ["timeSheet.canView"],
			// 			},
			// 			calculateOverMultipleProfiles && {
			// 				path: "overtimes",
			// 				title: "overtimes",
			// 				icon: TrendingUp,
			// 				permissions: ["timeSheet.canViewMultipleProfiles"],
			// 			},
			// 			(user?.otherProfileIds?.length > 0 && calculateOverMultipleProfiles) && {
			// 				path: "my-overtimes",
			// 				title: "my-overtimes",
			// 				icon: Table,
			// 				permissions: ["timeSheet.canViewMyMultipleProfiles"],
			// 			},
			// 			(!hideRetroAdjustment && {
			// 				path: "retro-adjustment",
			// 				title: "retro-adjustment",
			// 				icon: Calendar,
			// 				permissions: ["timeSheet.canViewRetro"]
			// 			}),
			// 			{
			// 				path: "my",
			// 				title: "my-timesheet",
			// 				icon: UserRound,
			// 				permissions: ["timeSheet.canViewMyTimesheet"],
			// 			},
			// 		]?.filter(Boolean) : [
			// 			{
			// 				index: true,
			// 				path: 'my',
			// 				title: 'my-timesheet',
			// 				icon: UserRound,
			// 				permissions: ['timeSheet.canViewMyTimesheet'],
			// 			},
			// 			(user?.otherProfileIds?.length > 0 && calculateOverMultipleProfiles) && {
			// 				path: "my-overtimes",
			// 				title: "my-overtimes",
			// 				icon: Table,
			// 				permissions: ["timeSheet.canViewMyMultipleProfiles"],
			// 			},
			// 		]?.filter(Boolean),
			// },
			// {
			// 	path: "scheduler",
			// 	icon: CalendarCheck,
			// 	access: canViewScheduler && modules.includes("schedule"),
			// 	children: canManageUsers
			// 		? [
			// 			{
			// 				index: true,
			// 				path: "team",
			// 				title: "my-team",
			// 				icon: Users,
			// 				permissions: ["schedule.canView"],
			// 			},
			// 			{
			// 				path: "batch-jobs",
			// 				title: "batch-jobs",
			// 				icon: FileOutput,
			// 				permissions: ["schedule.canView"],
			// 			},
			// 			{
			// 				path: "my",
			// 				title: "my-schedule",
			// 				icon: UserRound,
			// 				permissions: ["schedule.canViewMySchedule"],
			// 			},
			// 			{
			// 				path: "team-template",
			// 				title: "my-team-template",
			// 				icon: Users,
			// 				permissions: ["schedule.canView"],
			// 			},
			// 		]
			// 		: [
			// 			{
			// 				index: true,
			// 				path: "my",
			// 				title: "my-schedule",
			// 				icon: UserRound,
			// 				permissions: ["schedule.canViewMySchedule"],
			// 			},
			// 		],
			// },
			{
				path: "scheduler",
				icon: CalendarCheck,
				access: canViewScheduler && modules.includes("schedule"),
				children: canManageUsers
					? [
						{
							index: true,
							path: "team",
							title: "my-team",
							icon: Users,
							permissions: ["schedule.canView"],
						},
						{
							path: "batch-jobs",
							title: "batch-jobs",
							icon: FileOutput,
							permissions: ["schedule.canView"],
						},
						{
							path: "open-shifts",
							title: "open-shifts",
							icon: FolderSync,
							permissions: ["schedule.canView"],
						},
						{
							path: "my",
							title: "my-schedule",
							icon: UserRound,
							permissions: ["schedule.canViewMySchedule"],
						},
						{
							path: "team-template",
							title: "my-team-template",
							icon: LayoutTemplate,
							permissions: ["schedule.canView"],
						},
						// {
						// 	path: "settings",
						// 	title: "settings",
						// 	icon: Cog,
						// 	permissions: ["schedule.canView"],
						// },
					]
					: [
						{
							index: true,
							path: "my",
							title: "my-schedule",
							icon: UserRound,
							permissions: ["schedule.canViewMySchedule"],
						},
					],
			},
			{
				path: "time-off",
				icon: Timer,
				access: canViewTimeOff && modules.includes("timeOff"),
				children: [
					{
						path: "team",
						title: "my-team-timeoff",
						icon: Users,
						permissions: ["timeOff.canViewMyTeamTimeOff"]
					},
					{
						index: true,
						path: "my",
						title: "my-timeoff",
						icon: UserRound,
						permissions: ["timeOff.canViewMyTimeOff"],
					}
				],
			},
			{
				path: "request",
				icon: HandHelping,
				access: canViewRequest && modules.includes("requests"),
				children: [
					{
						index: true,
						path: "clock",
						title: "clock-request",
						icon: Clock,
						permissions: [
							"requests.shift.canView",
							"requests.shift.team.canView"
						],
						access: canViewClockRequest
					},
					{
						path: "pto",
						title: "pto-request",
						icon: CalendarDays,
						permissions: [
							"requests.absence.canView",
							"requests.absence.team.canView"
						],
						access: canViewPtoRequest
					},
					{
						path: "schedule",
						title: "schedule-request",
						icon: CalendarRange,
						permissions: [
							"requests.schedule.canView",
							"requests.schedule.team.canView"
						],
						access: canViewScheduleRequest
					},
					{
						path: "availability",
						title: "availability",
						icon: FileText,
						permissions: [
							"requests.availability.canView",
							"requests.availability.team.canView"
						],
						access: canViewAvailabilityRequest
					},
					{
						path: "timesheet",
						title: "timesheet",
						icon: Copy,
						permissions: [
							"requests.timesheet.mealPremiumWaive.canView",
							"requests.timesheet.mealPremiumWaive.team.canView",
							"requests.timesheet.missPunch.canView",
							"requests.timesheet.missPunch.team.canView",
						],
						access: canViewTimesheetRequest
					},
				]
			},
			{
				path: "reports",
				icon: Table,
				access: canViewReports && modules.includes("reports"),
				children: [
					{
						index: true,
						path: "custom-reports",
						title: "custom-reports",
						icon: FileText,
						permissions: ['reports.canView'],
					},
					{
						path: "exports",
						title: "export",
						icon: FileOutput,
						permissions: ["reports.canView"],
					},
					{
						path: 'old-system-reports',
						permissions: ['reports.canView'],
						title: 'old-system-reports',
						icon: FolderTree,
					},
					{
						path: 'old-custom-reports',
						title: 'old-custom-reports',
						permissions: ['reports.canView'],
						icon: FileCode,
					},
				],
			},
			{
				path: "billing",
				icon: Banknote,
				access: canViewBillingRate && modules.includes("billingRate"),
				children: [
					{
						index: true,
						path: "clients",
						permissions: ["billingRate.clients.canView"],
						icon: Users,
					},
					// {
					// 	path: "reports",
					// 	permissions: ["billingRate.reports.canView"],
					// 	icon: FileCode,
					// },
					// {
					// 	path: "invoices",
					// 	permissions: ["billingRate.invoices.canView"],
					// 	icon: faFileInvoiceDollar,
					// },
				],
			},
			{
				path: 'communication',
				icon: MessageSquare,
				access: canViewCommunication && modules.includes("communication"),
				children: [
					{
						index: true,
						path: 'messages',
						title: 'messages',
						permissions: ['communication'],
						icon: MessageCircle
					},
					{
						path: 'posts',
						title: 'posts',
						permissions: ['communication'],
						icon: Rows3
					}
				]
			},
			{
				type: "divider",
				access:
					!canViewGeneralSettings &&
					!canViewClockinSettings &&
					!canViewPayPolicies &&
					!canViewIntegration &&
					!canViewConfiguration &&
					!canViewScheduling &&
					!canViewRules &&
					!canViewCustomFields &&
					!canViewUserDemographics
			},
			{
				type: 'title',
				title: 'settings',
				access:
					!canViewGeneralSettings &&
					!canViewClockinSettings &&
					!canViewPayPolicies &&
					!canViewIntegration &&
					!canViewConfiguration &&
					!canViewScheduling &&
					!canViewRules &&
					!canViewCustomFields &&
					!canViewUserDemographics
			},
			{
				path: 'settings',
				inSidebar: false,
				children: [
					{
						path: 'general',
						icon: Settings2,
						access: canViewGeneralSettings,
						children: [
							{
								index: true,
								inSidebar: false,
							},
							{
								path: 'groups',
								icon: Layers,
								access: canViewGroup,
								children: [
									{ index: true, inSidebar: false },
									{
										path: ':id/levels',
										permissions: [
											'settings.general.organizationLevelGroup.organizationLevel.canView',
										],
									},
								],
							},
							{
								path: 'companies',
								icon: Building2,
								permissions: ['settings.general.companies.canView'],
							},
							{
								path: "scheduler",
								icon: CalendarDays,
								permissions: [
									"settings.general.canView"
								]
							},
							{
								path: "locations",
								icon: MapPin,
								permissions: ['settings.general.locations.canView'],
							},
							{
								path: 'employee-type',
								icon: Users,
								permissions: ['settings.general.employeeTypes.canView'],
							},
							{
								path: 'user-group',
								icon: Users,
								permissions: ['settings.general.userGroup.canView'],
							},
							{
								path: 'union',
								icon: Landmark,
								permissions: ['settings.general.union.canView'],
							},
							{
								path: 'projects',
								icon: ListTodo,
								permissions: ['settings.general.project.canView'],
							},
							{
								path: 'job-groups',
								icon: Rows3,
								permissions: ['settings.general.jobGroups.canView'],
							},
							{
								path: 'job-families',
								icon: Building,
								permissions: ['settings.general.jobFamilies.canView'],
							},
							...(modules.includes("pbj")
								? [
									{
										path: 'pbj-codes',
										icon: QrCodeIcon,
										permissions: ['settings.general.pbjCode.canView'],
									},
								]
								: []),
							{
								path: 'jobs',
								icon: Briefcase,
								permissions: ['settings.general.jobs.canView'],
							},
							{
								path: "working-shift",
								icon: Calendar,
								permissions: [
									"settings.general.workingShifts.canView",
								],
							},
							{
								path: "tasks",
								icon: ListChecks,
								permissions: ['settings.general.tasks.canView'],
							},
							{
								path: 'roles',
								icon: Presentation,
								permissions: ['settings.general.roles.canView'],
							},
							{
								path: "notification-settings",
								icon: Bell,
								permissions: ["notificationSettings.canManage"],
							},
							{
								path: "skills",
								icon: Sparkles,
								permissions: ["settings.general.skill.canView"],
							},
							{
								path: "certifications",
								icon: Award,
								permissions: ["settings.general.certification.canView"]
							},
							{
								path: "licenses",
								icon: IdCard,
								permissions: ["settings.general.license.canView"]
							},
						],
					},
					{
						path: 'pay-policy',
						icon: Receipt,
						access: canViewPayPolicies,
						children: [
							{
								path: 'pay-group',
								icon: UsersRound,
								permissions: ['settings.payPolicies.payGroup.canView'],
							},
							{
								path: 'pay-code',
								icon: FileText,
								permissions: ['settings.payPolicies.payCode.canView'],
							},
							{
								path: 'earning-group',
								icon: FileText,
								permissions: ['settings.payPolicies.earningGroup.canView'],
							},
							{
								path: 'budget',
								icon: CircleDollarSign,
								permissions: ['settings.payPolicies.budget.canView'],
							},
							{
								path: "holiday-calendar",
								icon: Gift,
								permissions: ['settings.payPolicies.holidays.canView'],
							},
							{
								path: 'time-off',
								icon: Timer,
								permissions: ['settings.payPolicies.timeOff.canView'],
							},
							{
								path: "calculation-rules",
								icon: Book,
								access: canViewCalRules,
								children: [
									{
										path: "meal-premium",
										icon: Coffee,
										permissions: [
											"settings.payPolicies.calculationGroup.canView",
										],
									},
									{
										path: "clock-exceptions",
										icon: Clock,
										permissions: [
											"settings.payPolicies.calculationGroup.canView",
										],
									},
									{
										path: "shift-premium",
										icon: Calculator,
										permissions: [
											"settings.payPolicies.calculationGroup.canView",
										],
									},
									{
										path: "amount-per-unit",
										icon: Square,
										permissions: [
											"settings.payPolicies.calculationGroup.canView",
										],
									},
								],
							},
							{
								path: "calculation-group",
								icon: Calculator,
								permissions: ['settings.payPolicies.calculationGroup.canView'],
							},
						],
					},
					{
						path: 'clock-in',
						icon: Clock,
						access: canViewClockinSettings,
						children: [
							{
								path: 'whitelist-number',
								icon: Phone,
								permissions: ['whitelistNumber.canView'],
							},
							{
								path: 'qr-code',
								icon: QrCode,
								permissions: ['qrCode.canView'],
							},
							{
								path: 'device',
								icon: Workflow,
								permissions: ['device.canView'],
							},
							{
								path: 'flyers',
								icon: Images,
								permissions: ['settings.flyer.canView'],
							},
							{
								path: "attestation",
								icon: Mail,
								permissions: ["settings.attestation.canView"],
							},
						],
					},
					{
						path: 'custom-fields',
						icon: Type,
						access: canViewCustomFields,
						children: [
							{
								path: 'timesheet',
								icon: UserRound,
								permissions: ['settings.general.customFields.canView'],
							},
							{
								path: "clock-in",
								icon: Clock,
								permissions: [
									"settings.general.customFields.canView",
								],
							},
							{
								path: "people",
								icon: User,
								permissions: ["settings.general.customFields.canView"],
							},
						],
					},
					{
						path: 'integration',
						icon: Network,
						access: canViewIntegration,
						children: [
							{
								path: 'ultipro',
								icon: UserCheck,
								permissions: ['settings.integration.ultipro.canView'],
							},
							{
								path: 'uta',
								icon: Presentation,
								permissions: ['settings.integration.uta.canView'],
							},
						],
					},
					{
						path: 'allocation-rules',
						icon: Book,
						access: canViewRules,
						children: [
							{
								path: 'calculation-group',
								icon: Calculator,
								permissions: ['settings.rules.calculationGroup.canView'],
							},
							{
								path: "time-off",
								icon: Timer,
								permissions: ["settings.rules.allocationRules.canView"],
							},
							{
								path: "holidays",
								icon: Gift,
								permissions: ["settings.rules.allocationRules.canView"],
							},
							{
								path: "locations",
								icon: MapPin,
								permissions: ["settings.rules.allocationRules.canView"],
							},
							{
								path: "pay-code",
								icon: FileText,
								permissions: ["settings.rules.allocationRules.canView"],
							}
						],
					},
					{
						path: 'job-scheduling',
						icon: CalendarDays,
						access: canViewScheduling,
						children: [
							{
								path: 'reports',
								icon: ClipboardList,
								permissions: ['settings.scheduling.reports.canView'],
							},
							{
								path: 'jobs',
								icon: BriefcaseBusiness,
								permissions: ['settings.scheduling.jobs.canView'],
							},
						],
					},
					{
						path: "user-demographics",
						icon: IdCard,
						access: canViewUserDemographics,
						children: [
							{
								path: "gender",
								icon: UsersRound,
								permissions: ["settings.userDemographic.canView"],
							},
							{
								path: "ethnicity",
								icon: FileText,
								permissions: ["settings.userDemographic.canView"],
							},
							{
								path: "employment-type",
								icon: BookUser,
								permissions: ["settings.userDemographic.canView"],
							},
						],
					},
					{
						path: "black-out-calendar",
						icon: Calendar,
						permissions: ["settings.configuration.canView"] //TODO permission
					},
					{
						path: "groupings",
						icon: Layers,
						permissions: ["settings.groupings.canView"],
					},
					{
						path: "configuration",
						icon: UserCog,
						access: canViewConfiguration,
						children: canManageUsers
							? [
								{
									path: "dashboard",
									icon: Home,
									permissions: ["settings.configuration.canView"]
								},
								{
									path: 'timesheet',
									icon: Copy,
									access: canViewConfiguration,
									children: [
										{
											path: 'general',
											icon: Settings2,
											title: 'general',
											permissions: ['settings.configuration.canView'],
										},
										{
											path: 'team',
											icon: Users,
											title: 'my-team',
											permissions: ['settings.configuration.canView'],
										},
										{
											path: 'manager-view',
											icon: ListTree,
											permissions: ['settings.configuration.canView'],
										},
										{
											path: 'my',
											icon: UserRound,
											title: 'my-timesheet',
											permissions: ['settings.configuration.canView'],
										},
										{
											path: 'rate',
											icon: Coins,
											permissions: ['settings.configuration.canView'],
										},
									],
								},
								{
									path: "schedule",
									icon: CalendarDays,
									access: canViewConfiguration,
									children: [
										{
											path: "general",
											icon: Settings2,
											title: "general",
											permissions: ["settings.configuration.canView"]
										},
										{
											path: "team",
											icon: Users,
											title: "team",
											permissions: ["settings.configuration.canView"]
										},
										{
											path: "my",
											icon: UserRound,
											title: "my",
											permissions: ["settings.configuration.canView"]
										},
										{
											path: "template",
											icon: ListTree,
											title: "template",
											permissions: ["settings.configuration.canView"]
										}
									]
								},
								{
									path: "reports",
									icon: Table,
									permissions: ["settings.configuration.canView"]
								},
								{
									path: "requests",
									icon: HandHelping,
									permissions: ["settings.configuration.canView"]
								},
								{
									path: "qr-code",
									icon: QrCode,
									permissions: ["settings.configuration.canView"]
								},
								{
									path: "api-connection",
									icon: Network,
									access: canViewConfiguration,
									children: [
										{
											path: "people",
											icon: User,
											permissions: ["settings.configuration.canView"],
										},
									]
								},
							]
							: [
								{
									path: 'timesheet',
									icon: Copy,
									access: canViewConfiguration,
									children: [
										{
											path: 'my',
											icon: UserRound,
											title: 'my-timesheet',
											permissions: ['settings.configuration.canView'],
										},
										{
											path: 'rate',
											icon: Coins,
											permissions: ['settings.configuration.canView'],
										},
									],
								},
								{
									path: "schedule",
									icon: CalendarDays,
									access: canViewConfiguration,
									children: [
										{
											path: "my",
											icon: UserRound,
											title: "my",
											permissions: ["settings.configuration.canView"]
										},
									]
								},
							]
					},
				],
			},
		], [
		canViewClockin,
		modules,
		canManageUsers,
		canViewTimesheet,
		canViewScheduler,
		canViewTimeOff,
		canViewRequest,
		canViewClockRequest,
		canViewPtoRequest,
		canViewScheduleRequest,
		canViewAvailabilityRequest,
		canViewTimesheetRequest,
		canViewReports,
		canViewBillingRate,
		canViewCommunication,
		canViewGeneralSettings,
		canViewClockinSettings,
		canViewPayPolicies,
		canViewIntegration,
		canViewConfiguration,
		canViewScheduling,
		canViewRules,
		canViewCustomFields,
		canViewUserDemographics,
		canViewGroup,
		canViewCalRules,
		user?.otherProfileIds?.length,
		calculateOverMultipleProfiles,
		hideRetroAdjustment
	]);
};
